import React, { useEffect, useState } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import { Button, Col, Collapse, Container, Row } from "reactstrap";
import { connect } from "react-redux";
import Autosuggest from "react-autosuggest";

import SearchOverlay from "./common/search-overlay";
import RFQModal from "../../Quote/RFQModal";

import { isLoggedIn } from "../../utils";
import { getRFQAccess } from "../../services/QuoteServices";
import { getSearchGroups } from "../../services/HomeServices";
import { getCart } from "../../actions/cart";

import logo from "../../assets/images/logo.png";
import CartIcon from "../../assets/images/carticon.png";
import Sidebar from "../../sidebar/Sidebar";

import "./HeaderResponsive.css";
import NavbarHorizontal from "./common/NavbarHorizontal";

function HeaderResponsive(props) {
  const { cart, getCart: fetchCart } = props;
  const [suggestionList, setSuggestionList] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [isRFQModalOpen, setIsRFQModalOpen] = useState(false);
  const [rfqModalVariant, setRfqModalVariant] = useState("noPermission");
  const [loadingAccessDetails, setLoadingAccessDetails] = useState(false);
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);

  const history = useHistory();
  const location = useLocation();

  useEffect(() => {
    if (isLoggedIn()) {
      fetchCart();
    }
  }, [fetchCart, location.pathname]);

  useEffect(() => {
    const unlisten = history.listen((nextLocation) => {
      if (!nextLocation.pathname.includes("search")) {
        setSearchValue("");
      }
    });

    return () => {
      unlisten();
    };
  }, [history]);

  useEffect(() => {
    if (!location.pathname.includes("/category/")) {
      setIsMobileFilterOpen(false);
    }
  }, [location.pathname]);

  const handleLogout = () => {
    localStorage.clear();
    window.location.href = "/public";
  };

  const handleRFQ = async () => {
    if (!isLoggedIn()) {
      setRfqModalVariant("noPermission");
      setIsRFQModalOpen(true);
      return;
    }

    const rawUser = localStorage.getItem("user");
    const user = rawUser ? JSON.parse(rawUser) : null;

    // Fast path: already has permission locally
    if (user?.has_rfq_permission) {
      history.push("/quote");
      return;
    }

    // Slow path: re-check from API for updated permissions
    setLoadingAccessDetails(true);
    try {
      const { data } = await getRFQAccess();
      const hasAccess = data?.has_rfq_permission;

      if (hasAccess) {
        history.push("/quote");
        user.has_rfq_permission = hasAccess;
        localStorage.setItem("user", JSON.stringify(user));
        return;
      }

      setRfqModalVariant("noPermission");
      setIsRFQModalOpen(true);
    } catch (error) {
      console.error("Error getting access details", error);
      setRfqModalVariant("noPermission");
      setIsRFQModalOpen(true);
    } finally {
      setLoadingAccessDetails(false);
    }
  };

  const getGroupList = (productName) => {
    getSearchGroups(productName)
      .then((response) => {
        if (response.status === 200) {
          setSuggestionList(response.data);
        }
      })
      .catch(() => {
        setSuggestionList([]);
      });
  };

  const onSuggestionsFetchRequested = ({ value }) => {
    if (!value || !value.trim()) {
      setSuggestionList([]);
      return;
    }
    getGroupList(value);
  };

  const onSuggestionsClearRequested = () => {
    setSuggestionList([]);
  };

  const onSuggestionSelected = (event, { suggestionValue }) => {
    setSearchValue(suggestionValue);
    history.push(`/search/${suggestionValue}`);
  };

  const onClickSearch = () => {
    if (!searchValue.trim()) {
      return;
    }
    history.push(`/search/${searchValue}`);
  };

  const getSuggestionValue = (suggestion) => suggestion.name;

  const renderSuggestion = (suggestion) => (
    <div className="suggestion-box">{suggestion.name}</div>
  );

  const handleChangeAutoSuggest = (event, { newValue }) => {
    if (newValue === "") {
      history.push("/");
    }
    setSearchValue(newValue);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && event.target.value !== "") {
      history.push(`/search/${event.target.value}`);
    }
  };

  const inputProps = {
    placeholder: "Search the product...",
    value: searchValue,
    onChange: handleChangeAutoSuggest,
    onKeyDown: handleKeyDown,
    className: "form-control",
  };

  const params = new URLSearchParams(location.search);
  const paramsMin = params.get("min");
  const paramsMax = params.get("max");

  return (
    <div>
      <header id="sticky">
        <div className="mobile-fix-option"></div>
        <div className="header_wrapper">
          <div className="main_header">
            <Container>
              <Row>
                <Col xs="12">
                  <div className="header-responsive-wrap d-flex align-items-center justify-content-center justify-content-sm-between flex-wrap">
                    <div className="logo order-1">
                      <Link to={{ pathname: "/public" }}>
                        <img src={logo} alt="BuyGroup" className="img-fluid" />
                      </Link>
                    </div>

                    <div className="nav-search responsive_search order-3 order-lg-2 w-100 mt-3 mt-lg-0">
                      <div className="input-group">
                        <Autosuggest
                          suggestions={suggestionList}
                          onSuggestionsFetchRequested={
                            onSuggestionsFetchRequested
                          }
                          onSuggestionsClearRequested={
                            onSuggestionsClearRequested
                          }
                          onSuggestionSelected={onSuggestionSelected}
                          getSuggestionValue={getSuggestionValue}
                          renderSuggestion={renderSuggestion}
                          inputProps={inputProps}
                        />

                        <div className="input-group-append">
                          <button
                            className="btn search_btn"
                            type="button"
                            onClick={onClickSearch}
                          >
                            <i className="fa fa-search"></i>
                          </button>
                        </div>
                      </div>

                      <button
                        className="btn filter_toggle_btn d-lg-none"
                        type="button"
                        onClick={() => setIsMobileFilterOpen((prev) => !prev)}
                        aria-expanded={isMobileFilterOpen}
                        aria-label="Toggle category filters"
                      >
                        <i className="fa fa-sliders"></i>
                      </button>
                    </div>

                    <div className="d-flex align-items-center header-actions order-2 order-lg-3">
                      <Button
                        className="btn get-quote-button"
                        onClick={handleRFQ}
                        disabled={loadingAccessDetails}
                      >
                        {loadingAccessDetails
                          ? "Please wait..."
                          : "Get a Quote"}
                      </Button>

                      <div className="d-flex align-items-center">
                        {isLoggedIn() ? (
                          <>
                            <div className="account_head_icon ml-3 main-navbar-cart">
                              <ul className="nav-menu">
                                <li>
                                  <Link
                                    to="/cart"
                                    className="cart_icon nav-link"
                                  >
                                    <div className="acc_icon_head">
                                      <img
                                        src={CartIcon}
                                        alt="Cart"
                                        className="img-fluid media"
                                      />
                                      <span className="cart_value">
                                        {cart.line_item_list &&
                                          cart.line_item_list.length}
                                      </span>
                                    </div>
                                    <p>My Cart</p>
                                  </Link>
                                  <ul
                                    className="nav-submenu show-div shopping-cart"
                                    style={{ display: "none" }}
                                  >
                                    {cart.length > 0
                                      ? cart.map((item, index) => (
                                          <li key={`cart-item-${index}`}>
                                            <div className="media">
                                              <div className="media-body">
                                                <h4>
                                                  <span>
                                                    {item.qty}&nbsp;x&nbsp;
                                                    {item.total}
                                                  </span>
                                                </h4>
                                              </div>
                                            </div>
                                            <div className="close-circle">
                                              <button
                                                type="button"
                                                className="btn btn-link p-0"
                                              >
                                                <i
                                                  className="fa fa-times"
                                                  aria-hidden="true"
                                                ></i>
                                              </button>
                                            </div>
                                          </li>
                                        ))
                                      : "No data Available"}
                                    <div>
                                      <li>
                                        <div className="buttons view-cart">
                                          <Link to="/cart">view cart</Link>
                                        </div>
                                      </li>
                                    </div>
                                  </ul>
                                </li>
                              </ul>
                            </div>
                            <ul className="d-flex align-items-center account_menus">
                              <li className="onhover-dropdown mobile-account">
                                <Link className="nav-link">
                                  <i className="fa fa-user pr-2"></i>
                                  <span className="account_text">Account</span>
                                  <span className="fa fa-angle-down ml-1"></span>
                                </Link>
                                <ul className="onhover-show-div">
                                  <li>
                                    <Link to="/dashboard">Dashboard</Link>
                                  </li>
                                  <li>
                                    <a href="#" onClick={handleLogout}>
                                      Logout
                                    </a>
                                  </li>
                                </ul>
                              </li>
                            </ul>
                          </>
                        ) : (
                          <ul className="d-flex align-items-center account_menus mb-0">
                            <li>
                              <Link to="/login">Sign In</Link>
                            </li>
                            <li>
                              <Link to="/register">Register</Link>
                            </li>
                          </ul>
                        )}
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>
            </Container>
          </div>

          <Collapse isOpen={isMobileFilterOpen} className="d-lg-none">
            <div className="mobile-filter-panel">
              <Container>
                <Row>
                  <Col xs="12">
                    <Sidebar minValue={paramsMin} maxValue={paramsMax} />
                  </Col>
                </Row>
              </Container>
            </div>
          </Collapse>

          <div className="header_wrapper_navbar d-none d-lg-block">
            <Container>
              <Row>
                <Col>
                  <div className="main-nav-center">
                    <NavbarHorizontal />
                  </div>
                </Col>
              </Row>
            </Container>
          </div>
        </div>
      </header>

      <SearchOverlay />
      <RFQModal
        open={isRFQModalOpen}
        onClose={() => setIsRFQModalOpen(false)}
        variant={rfqModalVariant}
      />
    </div>
  );
}

const mapStateToProps = (state) => {
  return {
    cart: state.cart.cart,
  };
};

const mapDispatchToProps = {
  getCart,
};

export default connect(mapStateToProps, mapDispatchToProps)(HeaderResponsive);
